import type { TestSummary } from './tests';

/**
 * The library as a shelf of whole tests.
 *
 * A test is three papers sharing a name — its Listening, its Reading, its
 * Writing — and that is the unit a centre works in: Test 3 is set on a class,
 * sat, and marked as one thing. The library used to be laid out the other way
 * up, three lists by module with a summary of the tests above them, which put
 * every paper on the screen twice: once as a chip in its test, once as a row
 * in its module. Deleting the rows deleted the papers the chips pointed at,
 * and a centre that meant to tidy away loose papers lost its tests.
 *
 * So: every paper belongs to exactly one place on the screen, and that place
 * is inside its test. What is left over — a paper named after no test — is
 * shown as exactly that, and is the only thing that can be tidied away on its
 * own.
 */

/**
 * The two halves of a library, in the order they are read.
 *
 * A centre does not keep one undifferentiated run of tests: the papers
 * recalled from real sittings are kept apart from the rest, taught
 * differently, and asked for by name. Actual tests come first because that is
 * what a centre reaches for.
 */
export const COLLECTIONS = ['ACTUAL', 'GENERAL'] as const;
export type Collection = (typeof COLLECTIONS)[number];

/**
 * Whether a test's name says it is an actual test.
 *
 * The same rule the server files by — kept in step with namesAnActualTest in
 * server/src/tests/collections.ts. The screen needs it so that renaming a test
 * into the other half can follow it there rather than leave a teacher looking
 * at the shelf it just left.
 */
export function namesAnActualTest(name: string | null | undefined): boolean {
  return /^\s*actual\b/i.test(name ?? '');
}

export const COLLECTION_LABEL: Record<Collection, string> = {
  ACTUAL: 'Actual Tests',
  GENERAL: 'Tests',
};

export const MODULES = ['LISTENING', 'READING', 'WRITING'] as const;
export type Module = (typeof MODULES)[number];

export const MODULE_LABEL: Record<Module, string> = {
  LISTENING: 'Listening',
  READING: 'Reading',
  WRITING: 'Writing',
};

export interface TestSlot {
  module: Module;
  /** The paper for this module, or null where the test has not got one yet. */
  paper: TestSummary | null;
}

export interface WholeTest {
  name: string;
  /**
   * Whether this centre owns the papers, or is borrowing them.
   *
   * Part of the grouping key rather than a fact about it: a centre with its
   * own "Test 1" and a lent "Test 1" has two tests, not one six-paper
   * chimera, and setting one on a class must not set the other.
   */
  owned: boolean;
  /** The centre that lent it, where a centre lent it. */
  sharedFrom: string | null;
  /** Whether it is the platform's own, which no centre may change. */
  platform: boolean;
  /** Whether every centre on the platform can teach from it. */
  shared: boolean;
  /** Which half of the library it is filed under. */
  collection: Collection;
  /** Which book it belongs to and where in that book: what the shelf is ordered by. */
  shelfmark: Shelfmark;
  /** One slot per module, in the order a candidate sits them. */
  slots: TestSlot[];
  /**
   * A second paper for a module the test already has one of. Rare, and always
   * a mistake or a replacement in progress — but shown, because a paper that
   * is on no screen cannot be corrected or deleted.
   */
  extras: TestSummary[];
  /** Every paper the test actually has, in module order. */
  papers: TestSummary[];
  /** The most recent import among them, for ordering tests with no number. */
  newest: string;
}

/**
 * Where a test sits on the shelf: which book, and where inside that book.
 *
 * A library of Cambridge books is numbered twice over — "Cambridge 21 Test 3"
 * is the third test of the twenty-first book. Reading only the last number, as
 * this used to, files every book's Test 3 together and scatters each book
 * across the shelf, which is what made a full library unnavigable.
 *
 * So the first number in the name is the book and the last is the test within
 * it. A name carrying one number has only a book — "Test 12" is the twelfth
 * test a centre made, not the twelfth of some book — which leaves a centre's
 * own numbering ordered exactly as it always was.
 */
export interface Shelfmark {
  /** The book, or the test's own number where it belongs to no book. */
  book: number | null;
  /** Where it comes in that book, where the name says. */
  test: number | null;
}

export function shelfmarkOf(name: string): Shelfmark {
  const runs = name.match(/\d+/g);
  if (!runs) return { book: null, test: null };
  const book = Number(runs[0]);
  const test = runs.length > 1 ? Number(runs[runs.length - 1]) : null;
  return {
    book: Number.isFinite(book) ? book : null,
    test: test !== null && Number.isFinite(test) ? test : null,
  };
}

/**
 * Newest book first, and inside a book the order the book itself prints.
 *
 * Cambridge 21's four tests, then Cambridge 20's four, and so on down: a
 * centre teaches from the newest book and asks for its tests by number, so
 * the shelf is read the way it is spoken about. Counting the books down but
 * the tests inside them up is not an inconsistency — 21 is newer than 20, and
 * Test 1 comes before Test 2 in both of them.
 *
 * A test whose name carries no number at all sits below the numbered ones,
 * newest import first, so it is still somewhere predictable.
 */
export function compareTests(a: WholeTest, b: WholeTest): number {
  const one = a.shelfmark;
  const two = b.shelfmark;

  if (one.book !== null && two.book !== null && one.book !== two.book) return two.book - one.book;
  if (one.book !== null && two.book === null) return -1;
  if (one.book === null && two.book !== null) return 1;

  if (one.test !== null && two.test !== null && one.test !== two.test) return one.test - two.test;
  // A bare "Cambridge 21" heads the book it names, above its own tests.
  if (one.test === null && two.test !== null) return -1;
  if (one.test !== null && two.test === null) return 1;

  if (a.newest !== b.newest) return a.newest < b.newest ? 1 : -1;
  return a.name.localeCompare(b.name);
}

const moduleRank = (module: string): number => {
  const at = (MODULES as readonly string[]).indexOf(module);
  return at === -1 ? MODULES.length : at;
};

/**
 * Splits the library into its whole tests and the papers that belong to none.
 *
 * Every paper handed in comes back out exactly once, in one of the two — the
 * property the old layout did not have, and the reason a deletion could reach
 * further than the row it was made from.
 */
export function groupIntoTests(papers: TestSummary[]): {
  tests: WholeTest[];
  loose: TestSummary[];
} {
  const byName = new Map<string, TestSummary[]>();
  const loose: TestSummary[] = [];

  for (const paper of papers) {
    const name = paper.setName?.trim();
    if (!name) {
      loose.push(paper);
      continue;
    }
    // Keyed by who owns it as well as by its name: two centres may both call
    // a test "Test 1", and one of them may have lent theirs to the platform.
    const key = `${paper.owned === false ? 'lent' : 'own'}\u0000${name}`;
    byName.set(key, [...(byName.get(key) ?? []), paper]);
  }

  const tests = [...byName.entries()].map(([key, members]) => {
    const name = key.slice(key.indexOf('\u0000') + 1);
    // Newest first within a module, so the paper a teacher just imported is
    // the one the test shows and any older one becomes the extra.
    const ordered = [...members].sort(
      (a, b) =>
        moduleRank(a.module) - moduleRank(b.module) ||
        (a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0),
    );

    const taken = new Set<string>();
    const slots: TestSlot[] = MODULES.map((module) => {
      const paper = ordered.find((p) => p.module === module && !taken.has(p.id)) ?? null;
      if (paper) taken.add(paper.id);
      return { module, paper };
    });

    return {
      name,
      owned: members[0]?.owned !== false,
      sharedFrom: members.find((paper) => paper.sharedFrom)?.sharedFrom ?? null,
      platform: members.some((paper) => paper.platform === true),
      /*
       * A test is an actual test when any of its papers says so. Papers are
       * filed one at a time underneath and a whole test is moved in one go, so
       * a test split across the two halves is a half-finished move — and the
       * half-moved test belongs where it was being moved to, not stranded
       * under the heading it was leaving.
       */
      collection: members.some((paper) => paper.collection === 'ACTUAL') ? ('ACTUAL' as const) : ('GENERAL' as const),
      shared: members.some((paper) => paper.shared === true),
      shelfmark: shelfmarkOf(name),
      slots,
      extras: ordered.filter((paper) => !taken.has(paper.id)),
      papers: ordered,
      newest: ordered.reduce((latest, paper) => (paper.createdAt > latest ? paper.createdAt : latest), ''),
    };
  });

  tests.sort(compareTests);
  return { tests, loose };
}

/**
 * The name of the book a test belongs to: everything up to the end of the
 * first number, which is the part of the name that identifies the book.
 *
 * "Cambridge 21 Test 3" is in "Cambridge 21", and so is "Cambridge 21
 * Academic Test 3"; "Cam-21 Test 3" is in "Cam-21" and "C21T3" in "C21". The
 * centre's own words are kept rather than tidied into a house style, because
 * the heading has to read as the thing the teacher already calls it.
 */
export function bookLabelOf(name: string): string | null {
  const first = /\d+/.exec(name);
  return first ? name.slice(0, first.index + first[0].length).trim() : null;
}

/** One book's worth of the shelf, under the heading it is read by. */
export interface Book {
  /** The heading, or null for the tests that belong to no book at all. */
  label: string | null;
  tests: WholeTest[];
}

/**
 * The shelf as books, in the order the tests were already in.
 *
 * Only a name numbered twice over names a book: "Cambridge 21 Test 3" is in
 * one, "Test 12" *is* one. So a centre that numbers its tests plainly gets a
 * single headingless run — exactly the list it had before books existed — and
 * a centre teaching from Cambridge gets one heading per book it owns.
 *
 * Grouping follows the order rather than imposing its own: the tests arrive
 * sorted, so a book's heading appears where its first test does and its tests
 * stay in the order the book prints them.
 */
export function booksOf(tests: WholeTest[]): Book[] {
  const books: Book[] = [];
  const byLabel = new Map<string, Book>();
  const strays: WholeTest[] = [];

  for (const test of tests) {
    const label = test.shelfmark.test === null ? null : bookLabelOf(test.name);
    if (label === null) {
      strays.push(test);
      continue;
    }
    const found = byLabel.get(label);
    if (found) {
      found.tests.push(test);
    } else {
      const book: Book = { label, tests: [test] };
      byLabel.set(label, book);
      books.push(book);
    }
  }

  // Last, under no heading: a test in no book is not a book of one.
  if (strays.length > 0) books.push({ label: null, tests: strays });
  return books;
}

/**
 * The shelf in two, each half in its own order.
 *
 * Both halves come back whether or not they hold anything, because the screen
 * decides what an empty half looks like — and a centre that has marked no
 * actual tests yet should not be told its library is broken.
 */
export function splitByCollection(tests: WholeTest[]): Record<Collection, WholeTest[]> {
  const split: Record<Collection, WholeTest[]> = { ACTUAL: [], GENERAL: [] };
  for (const test of tests) split[test.collection].push(test);
  for (const half of COLLECTIONS) split[half].sort(compareTests);
  return split;
}

/** What a test is missing, as the words that go under its name. */
export function whatIsMissing(test: WholeTest): string[] {
  return test.slots.filter((slot) => !slot.paper).map((slot) => MODULE_LABEL[slot.module]);
}
